import { all, call, fork, put, select } from 'redux-saga/effects';

import { sortArrayByProfileType, CONST_PROFILE_TYPE_SINGLE, CONST_PROFILE_TYPE_BUSINESS } from '../../helpers/user';
import UserClient from '../../api/UserClient';
import { userGetSuccessful, userGetFailed } from '../../redux/user/actions';
import { getPubKeyIntentSuccessful } from '../../redux/app/actions';
import { logoutRequest } from '../../redux/login/actions';
import { messagesShow } from '../../redux/messages/actions';
import { setLanguage } from '../../components/Contexts/Locale';
import { setDisplayRedirectToInvoices } from '../../redux/invoicesReplay/actions';
import { getInvoicesReplayDataSaga } from '../invoicesReplay/get';
import fetchCities from '../bookings/fetchCitiesRequest';
import { paymentProviderSelector } from '../../selectors/app';
import { getUserSubscriptionsSaga } from './setupMobilityPlanPage.saga';
import getMobilityPlansSaga from './mobilityPlans.saga';

export const extractDocuments = userDocuments => {
    const validDocuments = userDocuments.documents.filter(document => !!document.documentType.type);
    const documents = {};
    validDocuments.forEach(userDocument => (documents[userDocument.documentType.type] = userDocument));
    return documents;
};

export const extractDocumentTypes = userDocuments =>
    userDocuments.documentTypes.filter(documentType => !!documentType.type);

// "getUser"
export default function*({ payload = {} } = {}) {
    try {
        const { options } = payload;

        yield call(fetchCities);

        const [user, userProfile, userDocuments, cities, passwordStrength] = yield all([
            call(UserClient.getUser),
            call(UserClient.getUserProfile),
            call(UserClient.getUserDocuments),
            call(UserClient.getCities),
            call(UserClient.getPasswordStrength),
        ]);

        if (options) {
            if (options.includes('subscriptions')) {
                yield fork(getUserSubscriptionsSaga, userProfile);
            }

            if (options.includes('mobilityPlans')) {
                yield fork(getMobilityPlansSaga);
            }
        }

        const documents = extractDocuments(userDocuments);
        const documentTypes = extractDocumentTypes(userDocuments);
        const { documentByService } = userDocuments;

        // if the user has no entityId (multiprofile fleets),
        // then we use the entityId of the personal profile (single)
        let { entityId } = user;
        let businessProfile;
        let paymentDetailsBusiness;
        if (!entityId) {
            const personalProfile =
                user.profiles && user.profiles.find(profile => profile.type === CONST_PROFILE_TYPE_SINGLE);
            entityId = personalProfile && personalProfile.entityId;

            businessProfile =
                user.profiles && user.profiles.find(profile => profile.type === CONST_PROFILE_TYPE_BUSINESS);
        }

        const paymentDetails = yield call(UserClient.getPaymentDetails, entityId);
        if (businessProfile) {
            paymentDetailsBusiness = yield call(UserClient.getPaymentDetails, businessProfile.entityId);
        }

        if (user.profiles) {
            const hasOutstandingBalance = user.profiles.some(
                profile => profile.type === CONST_PROFILE_TYPE_SINGLE && profile.status === 'OUTSTANDING_BALANCE'
            );

            if (hasOutstandingBalance) {
                const invoicesReplayData = yield call(getInvoicesReplayDataSaga);

                if (invoicesReplayData.v_invoices && invoicesReplayData.v_invoices.length > 0) {
                    yield put(setDisplayRedirectToInvoices(true));
                }
            }
        }

        const paymentProvider = yield select(paymentProviderSelector);

        if (paymentProvider && paymentProvider !== 'stripe') {
            try {
                const pubKey = yield call(UserClient.getPublishableKeyForIntent);
                yield put(getPubKeyIntentSuccessful({ pubKey }));
            } catch (err) {
                yield console.error('Pub Key Intent error', err);
            }
        }

        const services = (cities || []).reduce((acc, city) => [...acc, ...city.services], []);
        user.profile = userProfile;

        user.profiles = sortArrayByProfileType(user.profiles);

        user.passwordStrength = passwordStrength;

        yield put(
            userGetSuccessful({
                user,
                paymentDetails,
                paymentDetailsBusiness,
                documents,
                documentTypes,
                documentByService,
                services,
            })
        );
        setLanguage(user.locale);
        return user;
    } catch (error) {
        if (error.code === 401) {
            yield put(logoutRequest());
        } else {
            yield put(userGetFailed());
            yield put(messagesShow('internalServerError', 'error'));
        }
        console.error('[user/get.js]', error);
    }

    return null;
}
